import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Gauge,
  Swords,
  PlusCircle,
  ChartLine,
  BrainCircuit,
  LogOut,
  User,
  Settings as SettingsIcon,
  UserPlus,
  Shield,
  Home as HomeIcon,
  Sparkles,
  Compass,
  BookOpen,
  LifeBuoy,
  HelpCircle,
  Newspaper,
  Info,
  PlayCircle,
  Mail,
  GraduationCap,
  Trophy,
} from "lucide-react";
import { ReactNode, useEffect, useState, type MouseEvent as ReactMouseEvent } from "react";
import { useTranslation } from "react-i18next";
import BrandBlock from "@/components/BrandBlock";
import TennisBallMenuIcon from "@/components/TennisBallMenuIcon";
import Footer from "@/components/Footer";
import InstallAppButton from "@/components/InstallAppButton";
import IosInstallNudge from "@/components/IosInstallNudge";
import DirectInvitationBell from "@/components/connected/DirectInvitationBell";
import { vpSnap } from "@/lib/viewportDiagnostics";

import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import InviteDialog from "@/components/InviteDialog";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";

export default function Layout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [inviteOpen, setInviteOpen] = useState(false);
  const isMatchEntryFlow =
    location.pathname === "/app/add" || location.pathname.startsWith("/app/edit/");

  const navigateWithMatchFlush = (
    event: ReactMouseEvent<HTMLAnchorElement>,
    to: string,
  ) => {
    if (!isMatchEntryFlow || to === location.pathname) return;

    event.preventDefault();

    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      if (ok) navigate(to);
    };

    const detail = {
      handled: false,
      done: finish,
    };
    const timeoutId = window.setTimeout(() => finish(true), 4000);

    window.dispatchEvent(
      new CustomEvent("quicklog:flush-save", {
        detail,
      }),
    );

    // /app/add has no refinement autosave listener yet; do not delay normal nav.
    if (!detail.handled) finish(true);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const nav = [
    { to: "/app/dashboard", label: t("nav.dashboard"), icon: Gauge, color: "from-primary to-primary-glow" },
    { to: "/app/matches", label: t("nav.matches"), icon: Swords, color: "from-primary-glow to-primary" },
    { to: "/app/add", label: t("nav.add"), icon: PlusCircle, color: "from-primary to-primary-glow" },
    { to: "/app/stats", label: t("nav.stats"), icon: ChartLine, color: "from-primary to-hold" },
    { to: "/app/insights", label: t("nav.insights"), icon: BrainCircuit, color: "from-primary to-break", fancy: true },
  ];

  const initials = (user?.email ?? "?").slice(0, 2).toUpperCase();

  // TEMPORARY: OAuth mobile scaling investigation.
  useEffect(() => {
    vpSnap("authenticated-layout-mount");
  }, []);

  useEffect(() => {
    document.title = "My Tennis Ledger (Tennis Log) – Track Matches, Stats & AI Insights";
    const desc = "Track your tennis matches, stats and performance with AI insights. Simple and powerful tennis score logging app.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
  }, []);

  return (
    <>
      <IosInstallNudge />
      <div className="min-h-screen flex flex-col">
        <header className="border-b border-border/60 bg-card/40 backdrop-blur-xl sticky top-0 z-40 app-header">
          <div className="container flex items-center justify-between gap-2 sm:gap-3 py-1.5 sm:py-3 min-h-0 sm:min-h-20 lscompact:!py-1 lscompact:!min-h-0">
            <Link to="/" aria-label="Tennis Log — My Tennis Ledger" className="flex items-center min-w-0 flex-1 lscompact:flex-none lscompact:shrink-0">
              <BrandBlock
                size={48}
                large
                logoClassName="app-logo sm:!w-[68px] sm:!h-[68px]"
                collapseOnCompactLandscape
              />
            </Link>

            {/* Compact landscape: the five primary destinations live in the header row. */}
            <nav
              aria-label="Primary"
              className="hidden lscompact:flex flex-1 min-w-0 shrink items-center justify-center gap-1 overflow-hidden"
            >

              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/app/dashboard"}
                  aria-label={n.label}
                  title={n.label}
                  onClick={(event) => navigateWithMatchFlush(event, n.to)}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 rounded-lg px-2 py-1.5 min-h-10 text-xs font-medium transition-colors ${
                      isActive ? "bg-secondary text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={`flex items-center justify-center w-6 h-6 rounded-md bg-gradient-to-br ${n.color} ${
                          isActive ? "opacity-100" : "opacity-70"
                        }`}
                        style={
                          n.fancy
                            ? {
                                background:
                                  "conic-gradient(from 180deg at 50% 50%, hsl(310 95% 60%), hsl(265 95% 65%), hsl(200 95% 60%), hsl(160 90% 55%), hsl(75 100% 60%), hsl(45 100% 60%), hsl(0 95% 65%), hsl(310 95% 60%))",
                              }
                            : undefined
                        }
                      >
                        <n.icon className="w-4 h-4 text-primary-foreground" strokeWidth={2.4} />
                      </span>
                      <span className="hidden lscompact:min-[900px]:inline whitespace-nowrap">{n.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            <InstallAppButton variant="header" className="shrink-0 lscompact:hidden" />
            <div className="flex items-center gap-1 shrink-0">
              <div className="flex flex-col items-stretch gap-0.5 lscompact:flex-row lscompact:items-center">

                <LanguageSwitcher />
                <ThemeSwitcher />
              </div>
              {user && (
                // Portrait: position notifications directly below the tennis-ball
                // account menu. Compact landscape stays side-by-side to conserve height.
                <div className="flex shrink-0 flex-col items-center gap-0.5 lscompact:flex-row-reverse lscompact:gap-1" data-testid="header-account-notifications">
                  <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Account"
                      aria-haspopup="menu"
                      className="relative rounded-full w-11 h-11 p-0 overflow-hidden border border-primary/40 bg-secondary/60 hover:bg-primary/10 active:scale-95 transition shadow-glow"
                    >
                      <TennisBallMenuIcon className="w-9 h-9" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 max-h-[70vh] overflow-y-auto">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span className="text-xs truncate">{user.email}</span>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/app/settings")}>
                      <SettingsIcon className="w-4 h-4 mr-2" /> {t("nav.settings")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/app/rankings")}>
                      <Trophy className="w-4 h-4 mr-2" /> {t("nav.rankings")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setInviteOpen(true)}>
                      <UserPlus className="w-4 h-4 mr-2" /> {t("nav.invite")}
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => navigate("/app/admin")} className="text-primary">
                        <Shield className="w-4 h-4 mr-2" /> Admin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      Browse
                    </DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => navigate("/")}>
                      <HomeIcon className="w-4 h-4 mr-2" /> Home
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/features")}>
                      <Sparkles className="w-4 h-4 mr-2" /> Features
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/how-it-works")}>
                      <Compass className="w-4 h-4 mr-2" /> How it Works
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/academy")}>
                      <GraduationCap className="w-4 h-4 mr-2" /> Academy
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/tennis-statistics-guide")}>
                      <BookOpen className="w-4 h-4 mr-2" /> Guide
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/help")}>
                      <LifeBuoy className="w-4 h-4 mr-2" /> Help Center
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/faq")}>
                      <HelpCircle className="w-4 h-4 mr-2" /> FAQ
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/blog")}>
                      <Newspaper className="w-4 h-4 mr-2" /> Blog
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/about")}>
                      <Info className="w-4 h-4 mr-2" /> About
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/demo")}>
                      <PlayCircle className="w-4 h-4 mr-2" /> Demo
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/contact")}>
                      <Mail className="w-4 h-4 mr-2" /> Contact
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={signOut} className="text-destructive">
                      <LogOut className="w-4 h-4 mr-2" /> {t("nav.logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                  </DropdownMenu>
                  <DirectInvitationBell />
                </div>
              )}
            </div>
          </div>
          <div className="container hidden md:block pb-2 lscompact:!hidden">
            <nav className="hidden md:flex items-center gap-1 flex-wrap">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/app/dashboard"}
                  onClick={(event) => navigateWithMatchFlush(event, n.to)}
                  className={({ isActive }) =>
                    `group relative px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                      isActive
                        ? "bg-secondary text-primary shadow-glow"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={`flex items-center justify-center w-7 h-7 rounded-md bg-gradient-to-br ${n.color} ${
                          isActive ? "opacity-100 shadow-glow" : "opacity-70 group-hover:opacity-100"
                        } transition-opacity`}
                        style={
                          n.fancy
                            ? {
                                background:
                                  "conic-gradient(from 180deg at 50% 50%, hsl(310 95% 60%), hsl(265 95% 65%), hsl(200 95% 60%), hsl(160 90% 55%), hsl(75 100% 60%), hsl(45 100% 60%), hsl(0 95% 65%), hsl(310 95% 60%))",
                                boxShadow: "0 0 10px hsl(280 90% 60% / 0.6), 0 0 16px hsl(180 90% 55% / 0.45)",
                              }
                            : undefined
                        }
                      >
                        <n.icon className="w-4 h-4 text-primary-foreground drop-shadow" strokeWidth={2.4} />
                      </span>
                      <span className="tracking-wide">{n.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          </div>
          <nav className="md:hidden lscompact:!hidden flex items-center justify-around border-t border-border/60 px-2 py-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/app/dashboard"}
                onClick={(event) => navigateWithMatchFlush(event, n.to)}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 px-2 py-0.5 rounded-md text-[10px] tracking-wide ${
                    isActive ? "text-primary" : "text-muted-foreground"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br ${n.color} ${
                        isActive ? "opacity-100 shadow-glow" : "opacity-70"
                      }`}
                      style={
                        n.fancy
                          ? {
                              background:
                                "conic-gradient(from 180deg at 50% 50%, hsl(310 95% 60%), hsl(265 95% 65%), hsl(200 95% 60%), hsl(160 90% 55%), hsl(75 100% 60%), hsl(45 100% 60%), hsl(0 95% 65%), hsl(310 95% 60%))",
                              boxShadow: "0 0 10px hsl(280 90% 60% / 0.6), 0 0 16px hsl(180 90% 55% / 0.45)",
                            }
                          : undefined
                      }
                    >
                      <n.icon className="w-4 h-4 text-primary-foreground" strokeWidth={2.4} />
                    </span>
                    {n.label}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="flex-1 container py-8 lscompact:py-3 animate-fade-in space-y-6 lscompact:space-y-3">
          {children}
        </main>
        {isMatchEntryFlow ? (
          <div className="hidden md:block">
            <Footer />
          </div>
        ) : (
          <Footer />
        )}
        <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      </div>
    </>
  );
}
